import { Pie, PieChart as RCPieChart, ResponsiveContainer } from "recharts";
import { colors } from "../../tokens/colors";

export type PieChartData = {
    name: string;
    value: number;
    description?: string;
};

interface PieChartProps {
    data: PieChartData[];
    /** Show raw counts alongside percentages in the persistent legend. */
    showValues?: boolean;
}

const COLORS = [
    colors.secondary.DEFAULT,
    colors.primary.DEFAULT,
    colors.success.DEFAULT,
    colors.warning.DEFAULT,
    colors.destructive.DEFAULT,
];

/** Keep values outside the SVG: small and zero slices remain readable, including in exports. */
export const PieChart: React.FC<PieChartProps> = ({ data, showValues = false }) => {
    const total = data.reduce((sum, entry) => sum + entry.value, 0);
    const coloredData = data.map((entry, index) => ({ ...entry, fill: COLORS[index % COLORS.length] }));
    return (
        <div className="w-full min-w-0">
            <div aria-hidden="true" className="relative mx-auto h-56 w-full max-w-sm">
                <ResponsiveContainer width="100%" height="100%">
                    <RCPieChart accessibilityLayer={false}>
                        <Pie
                            data={coloredData.filter((entry) => entry.value > 0)}
                            dataKey="value"
                            innerRadius="65%"
                            outerRadius="90%"
                            stroke={colors.background}
                            strokeWidth={2}
                            // Export selections rerender the page; numbers must never wait on an animation.
                            isAnimationActive={false}
                        />
                    </RCPieChart>
                </ResponsiveContainer>
                {showValues && (
                    <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                        <span className="text-foreground text-2xl font-semibold tabular-nums">
                            {total.toLocaleString()}
                        </span>
                        <span className="text-muted-foreground text-xs">Total</span>
                    </div>
                )}
            </div>
            <ul aria-label="Chart breakdown" className="m-0 list-none space-y-3 p-0">
                {coloredData.map((entry) => (
                    <li key={entry.name} className="flex items-start gap-3 text-sm">
                        <span
                            aria-hidden="true"
                            className="mt-1 h-3 w-3 shrink-0 rounded-sm"
                            style={{ backgroundColor: entry.fill }}
                        />
                        <div className="min-w-0 flex-1">
                            <span className="text-foreground">{entry.name}</span>
                            {entry.description && <p className="text-muted-foreground text-xs">{entry.description}</p>}
                        </div>
                        {showValues && (
                            <span className="text-foreground font-medium tabular-nums">
                                {entry.value.toLocaleString()}
                            </span>
                        )}
                        <span className="text-muted-foreground w-12 text-right tabular-nums">
                            {total > 0 ? `${((entry.value / total) * 100).toFixed(1)}%` : "0.0%"}
                        </span>
                    </li>
                ))}
            </ul>
        </div>
    );
};
